import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { TextInput } from 'src/design/atoms/TextInput';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DefaultFolderDto } from 'src/api/FiretrolApiClient';
import { useOffices } from 'src/api/queries/organization/useOffices';
import { useDocumentTemplateDetail } from 'src/api/queries/documentTemplates/useDocumentTemplateDetail';
import { useDeleteDocumentTemplateFolder } from 'src/api/queries/documentTemplates/useDocumentTemplateFolderMutations';
import {
  useDeleteDocumentTemplate,
  useUpdateDocumentTemplate,
} from 'src/api/queries/documentTemplates/useDocumentTemplateMutations';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { InputLabel } from 'src/design/atoms/InputLabel';
import { DeleteButton } from 'src/design/atoms/DeleteButton';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { Modal } from 'src/design/molecules/Modal';
import { Header } from 'src/design/organisms/Header';
import { OfficeSelector } from 'src/app/selectors/OfficeSelectorSingle';
import { showSuccessToast } from 'src/utils';
import { CreateFolderDialog } from 'src/routes/Settings/Documents/CreateFolderDialog';
import { FolderTree } from 'src/app/documents/FolderTree';
import { findFolderPath } from 'src/app/documents/folderPath';
import { ROOT_FOLDER_ID } from 'src/app/documents/documentConstants';

export const CreateDocumentTemplate: React.FC = () => {
  const { id: templateId = '' } = useParams();
  const navigate = useNavigate();
  const { isLoading: loadingOffices } = useOffices();
  const {
    data,
    isLoading: loading,
    isFetching,
  } = useDocumentTemplateDetail(templateId);
  const folders = React.useMemo(() => data?.folders ?? [], [data]);
  const updateTemplateMutation = useUpdateDocumentTemplate();
  const deleteTemplateMutation = useDeleteDocumentTemplate();
  const removeFolderMutation = useDeleteDocumentTemplateFolder();
  const [createFolderOpen, setCreateFolderOpen] = React.useState(false);
  const [editFolderOpen, setEditFolderOpen] = React.useState(false);
  const [selectedFolder, setSelectedFolder] =
    React.useState<DefaultFolderDto | null>(null);
  const [breadcrumbs, setBreadcrumbs] = React.useState<DefaultFolderDto[]>([]);
  const [name, setName] = React.useState('');
  const [officeId, setOfficeId] = React.useState<number | null>(null);

  const tname = data?.template.name ?? '';
  const originalOfficeId = data?.template.officeId ?? null;

  // The name/office inputs are draft state; reseed them whenever the server
  // copy changes so a silent refetch doesn't strand an edit mid-flight.
  React.useEffect(() => {
    if (!data) return;
    setName(data.template.name);
    setOfficeId(data.template.officeId);
    setSelectedFolder(null);
    setBreadcrumbs([]);
  }, [data?.template.name, data?.template.officeId]);

  const closeCreateDialog = () => {
    setCreateFolderOpen(false);
  };

  const closeEditDialog = () => {
    setEditFolderOpen(false);
  };

  const onFolderSelected = (id: string) => {
    const crumbs = id === ROOT_FOLDER_ID ? [] : findFolderPath(folders, id);
    setSelectedFolder(crumbs[crumbs.length - 1] ?? null);
    setBreadcrumbs(crumbs);
  };

  const removeFolder = async () => {
    if (!selectedFolder) return;
    await removeFolderMutation.mutateAsync(selectedFolder.id);
    setSelectedFolder(null);
    setBreadcrumbs([]);
  };

  const updateTemplate = async () => {
    await updateTemplateMutation.mutateAsync({
      templateId,
      request: { id: templateId, name, officeId },
    });
    showSuccessToast(`"${name}" updated!`);
  };
  const deleteTemplate = async () => {
    await deleteTemplateMutation.mutateAsync(templateId);
    showSuccessToast(`"${name}" deleted!`);
    navigate('/settings/documents');
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      updateTemplate();
    }
  };

  const lockButton = (
    <SaveButton
      onClick={updateTemplate}
      disabled={tname === name && originalOfficeId === officeId}
    />
  );
  return (
    <>
      <Header
        title="Documents"
        breadcrumbProps={{
          items: [
            { text: 'Settings' },
            { text: 'Documents', href: '/settings/documents' },
            { text: 'Create Template' },
          ],
        }}
        helpText="Configure the default document template's folder structure and office assignment."
      />

      <Stack gap="md">
        <Paper className="m-b-15">
          <Group gap="sm" justify="left" mb="sm">
            <BasicButton
              color="gray.5"
              onClick={() => navigate('/settings/documents')}
            >
              Back
            </BasicButton>

            <InlineConfirmButton
              color="red"
              confirmButtonProps={{
                children: 'Confirm Delete',
              }}
              onConfirmClick={deleteTemplate}
            >
              Delete Template
            </InlineConfirmButton>
          </Group>
          {!loadingOffices && !loading ? (
            <>
              <InputLabel className="m-b-0">Office</InputLabel>
              <OfficeSelector
                initialOfficeId={officeId}
                initialValue={''}
                onChange={() => undefined}
                onOfficeChange={(office) => {
                  setOfficeId(office ? office.id : null);
                }}
              />
            </>
          ) : (
            <div>Loading offices...</div>
          )}

          <TextInput
            className="m-b-15 m-t-15"
            label="Template Name"
            onKeyDown={handleKeyDown}
            placeholder="Enter template name"
            onChange={(e) => setName(e.target.value)}
            value={name}
          />
          {lockButton}
        </Paper>
        <Paper style={{ minHeight: 500 }}>
          <Grid>
            <Grid.Col span={4}>
              <Group gap="xs">
                <h3 className="m-t-0">Default Folders</h3>
                {isFetching && !loading && <Loader size="xs" />}
              </Group>
            </Grid.Col>
            <Grid.Col span={8} className="text-right">
              {selectedFolder ? (
                <>
                  <BasicButton
                    size="xs"
                    onClick={() => setCreateFolderOpen(true)}
                  >
                    Add Subfolder to{' '}
                    <b className="m-l-5">{selectedFolder.name}</b>
                  </BasicButton>{' '}
                  <BasicButton
                    size="xs"
                    onClick={() => setEditFolderOpen(true)}
                  >
                    Edit <b className="m-l-5">{selectedFolder.name}</b>
                  </BasicButton>{' '}
                  <DeleteButton size="xs" onClick={removeFolder} />
                </>
              ) : (
                <BasicButton
                  size="xs"
                  onClick={() => setCreateFolderOpen(true)}
                >
                  Create Folder
                </BasicButton>
              )}
            </Grid.Col>
          </Grid>

          {loading ? (
            <div className="p-t-50 text-center">
              <Loader />
            </div>
          ) : (
            <FolderTree
              selectedFolderId={selectedFolder?.id ?? ROOT_FOLDER_ID}
              onFolderSelected={onFolderSelected}
              folders={folders}
              rootLabel="Documents"
            />
          )}
        </Paper>
      </Stack>
      <Modal
        opened={createFolderOpen}
        onClose={closeCreateDialog}
        title="Create Folder"
      >
        {createFolderOpen && (
          <CreateFolderDialog
            breadcrumbs={breadcrumbs}
            onClose={closeCreateDialog}
            selectedFolder={selectedFolder}
            templateId={templateId}
          />
        )}
      </Modal>
      <Modal
        opened={editFolderOpen}
        onClose={closeEditDialog}
        title="Edit Folder"
      >
        {editFolderOpen && (
          <CreateFolderDialog
            edit
            templateId={templateId}
            breadcrumbs={breadcrumbs}
            onClose={closeEditDialog}
            selectedFolder={selectedFolder}
          />
        )}
      </Modal>
    </>
  );
};
